var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AQuillEditorActionsEnum } from '@/components/AQuillEditor/AQuillEditor';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import AQuillEditor from '@/components/AQuillEditor/AQuillEditor.vue';
import ErrorHandler from '@/support/ErrorHandler';
import { formattedDate } from '@/support/Date';
import { StatusColorMap } from '@/support/StatusColorMap';
import { RpcClient } from '@/http';
import LabelValue from '@/components/LabelValue.vue';
import { uniq } from 'lodash';
import { SubjectUserNote } from '@/models/SubjectUserNote';
let UserGoalNotesDialog = class UserGoalNotesDialog extends Vue {
    constructor() {
        super(...arguments);
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.isLoading = true;
        this.isLoadingNotes = false;
        this.AQuillEditorActionsEnum = AQuillEditorActionsEnum;
        this.formattedDate = formattedDate;
        this.statusColorMap = StatusColorMap;
        this.noteIsDirty = false;
        this.noteStatus = 'saved';
        this.note = '';
        this.initialNote = '';
        this.noteObjects = [];
        this.noteToEdit = null;
        this.selectedSubjectsForAllStudents = [];
        this.selectedStudentsForAllSubjects = [];
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks
    mounted() {
        this.initialize();
    }
    async initialize() {
        this.isLoading = true;
        if (this.isAddingForAllStudents) {
            this.selectedStudentsForAllSubjects = this.students;
        }
        if (this.isBulk) {
            // No notes to find
        }
        else {
            await this.findNotes();
        }
        this.isLoading = false;
    }
    // #endregion
    // #region Event handlers
    onSaveGoalNote() {
        if (this.noteToEdit) {
            this.updateNote();
        }
        else {
            this.createNote();
        }
    }
    onCancelEditNote() {
        this.note = '';
        this.initialNote = '';
        this.noteToEdit = null;
        this.noteIsDirty = false;
    }
    onEditNote(note) {
        this.noteToEdit = note;
        this.note = note.note;
        this.initialNote = note.note;
    }
    onDeleteNote(note) {
        this.deleteNote(note);
    }
    // #endregion
    // #region Class methods
    close() {
        this.$emit('close');
        this.clear();
    }
    clear() {
        this.noteObjects = [];
        this.note = '';
        this.initialNote = '';
        this.noteIsDirty = false;
        this.noteStatus = 'saved';
        this.noteToEdit = null;
        this.selectedSubjectsForAllStudents = [];
    }
    sortNotes() {
        if (!this.noteObjects || this.noteObjects.length === 0) {
            return;
        }
        this.noteObjects.sort((a, b) => {
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
    }
    canEdit(note) {
        return note.user.id === this.user.id;
    }
    checkForEmptyHtml(content) {
        if (!content)
            return true;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        return textContent.trim() === '';
    }
    createCreateNotePayload() {
        const payload = {
            note: this.note,
            subject_students: [],
        };
        if (this.isAddingForAllStudents) {
            for (const subject of this.selectedSubjectsForAllStudents) {
                for (const student of this.selectedStudentsForAllSubjects) {
                    payload.subject_students.push({
                        subject_id: subject.id,
                        user_id: student.id,
                    });
                }
            }
        }
        else {
            for (const userSubject of this.userSubjects) {
                payload.subject_students.push({
                    subject_id: userSubject.tempSubjectId,
                    user_id: userSubject.tempStudentId,
                });
            }
        }
        return payload;
    }
    // #endregion
    // #region Async methods (api calls only)
    async findNotes() {
        this.noteObjects =
            this.userSubjects[0] && this.userSubjects[0].notes && this.userSubjects[0].notes.notesObject
                ? this.userSubjects[0].notes.notesObject
                : [];
        this.sortNotes();
    }
    async createNote() {
        const payload = this.createCreateNotePayload();
        try {
            const note = await new RpcClient().execute('add-note-to-all-students-or-subjects', payload);
            const emitPayload = {
                action: 'create',
                note,
                affectedSubjectIds: payload.subject_students.map((selectedSubject) => selectedSubject.subject_id),
                affectedStudentIds: payload.subject_students.map((selectedStudent) => selectedStudent.user_id),
                userSubjects: this.userSubjects || [],
            };
            this.$emit('notesUpdated', emitPayload);
            if (!this.isBulk && !this.isAddingForAllStudents) {
                this.note = '';
                this.initialNote = '';
                this.noteIsDirty = false;
                this.noteStatus = 'saved';
                this.selectedSubjectsForAllStudents = [];
            }
            else {
                this.close();
            }
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async updateNote() {
        if (!this.noteToEdit) {
            return;
        }
        try {
            const editedNote = new SubjectUserNote(this.noteToEdit);
            editedNote.note = this.note;
            await editedNote.update();
            const emitPayload = {
                action: 'update',
                note: editedNote,
                affectedSubjectIds: this.userSubjects.map((userSubject) => userSubject.tempSubjectId || ''),
                affectedStudentIds: this.userSubjects.map((userSubject) => userSubject.tempStudentId || ''),
                userSubjects: this.userSubjects || [],
            };
            this.$emit('notesUpdated', emitPayload);
            this.onCancelEditNote();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async deleteNote(note) {
        try {
            const noteToDelete = new SubjectUserNote(note);
            await noteToDelete.delete();
            const emitPayload = {
                action: 'delete',
                note: noteToDelete,
                affectedSubjectIds: this.userSubjects.map((userSubject) => userSubject.tempSubjectId || ''),
                affectedStudentIds: this.userSubjects.map((userSubject) => userSubject.tempStudentId || ''),
                userSubjects: this.userSubjects || [],
            };
            this.$emit('notesUpdated', emitPayload);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    // #endregion
    // #region Getters & Setters
    get isBulk() {
        return this.userSubjects.length > 1;
    }
    get hasNotes() {
        return this.noteObjects && this.noteObjects.length > 0;
    }
    get noteStatusLabel() {
        return this.noteStatus === 'saved' ? this.$tc('Saved') : this.$tc('Unsaved changes');
    }
    get editorOptions() {
        return {
            placeholder: this.$t('Add a note...'),
        };
    }
    get subjectStatusLabel() {
        if (this.isBulk) {
            return '-';
        }
        if (!this.userSubjects ||
            !this.userSubjects[0] ||
            !this.userSubjects[0].status ||
            !this.userSubjects[0].label_group ||
            !this.userSubjects[0].label_group.labels) {
            return this.$tc(`idp.status.inactive`, 2);
        }
        const label = this.userSubjects[0].label_group.labels.find((existingLabel) => {
            return existingLabel.key === this.userSubjects[0].status;
        });
        return label && label.value ? label.value : this.$tc(`idp.status.inactive`, 2);
    }
    get user() {
        return this.$store.state.Auth;
    }
    get dialogTitle() {
        if (this.isAddingForAllStudents) {
            return this.$tc('Add note for all students');
        }
        if (this.isBulk) {
            return this.$tc('Add note to selected goals');
        }
        const subject = this.subjects.find((subject) => {
            return (subject.id === this.userSubjects[0].tempSubjectId ||
                subject.id === this.userSubjects[0].tempUserSubjectId);
        });
        return subject && subject.name ? subject.name : '-';
    }
    get dialogSubTitle() {
        if (this.isAddingForAllStudents) {
            return '';
        }
        if (this.isBulk) {
            return '';
        }
        const student = this.students.find((student) => {
            return student.id === this.userSubjects[0].tempStudentId;
        });
        return student && student.name ? student.name : '-';
    }
    get userSubjectStyles() {
        if (this.isBulk) {
            return {};
        }
        if (!this.userSubjects[0] || !this.userSubjects[0].status) {
            return { 'background-color': this.statusColorMap.inactive };
        }
        return { 'background-color': this.statusColorMap[this.userSubjects[0].status] };
    }
    get selectedSubjectsLabel() {
        const subjects = this.userSubjects.map((userSubject) => {
            const subject = this.subjects.find((subject) => {
                return subject.id === userSubject.tempSubjectId;
            });
            return subject && subject.name ? subject.name : '-';
        });
        return uniq(subjects).join(', ');
    }
    get selectedStudentsLabel() {
        if (this.isAddingForAllStudents) {
            return this.students.map((student) => student.name || '-').join(', ');
        }
        const students = this.userSubjects.map((userSubject) => {
            const student = this.students.find((student) => {
                return student.id === userSubject.tempStudentId;
            });
            return student && student.name ? student.name : '-';
        });
        return uniq(students).join(', ');
    }
    get canSaveNote() {
        if (this.isAddingForAllStudents) {
            return this.noteIsDirty && this.selectedSubjectsForAllStudents.length > 0;
        }
        return this.noteIsDirty;
    }
    // #endregion
    // #region @Watchers
    onNoteChange() {
        const noteHasEmptyHtml = this.checkForEmptyHtml(this.note);
        if (noteHasEmptyHtml && this.initialNote === '') {
            this.noteIsDirty = false;
        }
        else {
            this.noteIsDirty = this.note !== this.initialNote;
        }
    }
    onNoteIsDirtyChange(newValue) {
        this.noteStatus = newValue ? 'dirty' : 'saved';
    }
};
__decorate([
    Prop({ required: false }),
    __metadata("design:type", Array)
], UserGoalNotesDialog.prototype, "userSubjects", void 0);
__decorate([
    Prop({ required: false }),
    __metadata("design:type", Array)
], UserGoalNotesDialog.prototype, "subjects", void 0);
__decorate([
    Prop({ required: true }),
    __metadata("design:type", Array)
], UserGoalNotesDialog.prototype, "students", void 0);
__decorate([
    Prop({ default: false }),
    __metadata("design:type", Boolean)
], UserGoalNotesDialog.prototype, "isAddingForAllStudents", void 0);
__decorate([
    Watch('note'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], UserGoalNotesDialog.prototype, "onNoteChange", null);
__decorate([
    Watch('noteIsDirty'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], UserGoalNotesDialog.prototype, "onNoteIsDirtyChange", null);
UserGoalNotesDialog = __decorate([
    Component({
        components: {
            AQuillEditor,
            LabelValue,
        },
    })
], UserGoalNotesDialog);
export default UserGoalNotesDialog;
// #endregion
// #region Constants
// #endregion
